<template>
  <v-overlay 
    :model-value="modelValue" 
    class="d-flex align-center justify-center"
    persistent
    style="z-index: 9999;"
  >
    <v-card 
      class="loading-card pa-4 md:pa-8 text-center" 
      elevation="12"
      rounded="xl"
      max-width="500"
      width="95vw"
    >
      <!-- Loading Animation -->
      <div class="mb-4 md:mb-6">
        <div class="loading-animation-container">
          <!-- Animated QR Code Icon -->
          <div class="qr-icon-container">
            <v-icon 
              :size="$vuetify.display.mobile ? 60 : 80" 
              color="primary" 
              class="qr-icon-animation"
            >
              mdi-qrcode
            </v-icon>
            
            <!-- Spinning progress circle around the QR icon -->
            <v-progress-circular
              :size="$vuetify.display.mobile ? 90 : 120"
              :width="3"
              color="primary"
              indeterminate
              class="progress-ring"
            />
          </div>

        </div>
      </div>

      <!-- Loading Text -->
      <div class="mb-3 md:mb-4">
        <h3 class="text-h6 md:text-h5 font-weight-bold text-primary mb-2">
          {{ loadingTitle }}
        </h3>
        <p class="text-body-2 md:text-body-1 text-grey-darken-1 mb-2 md:mb-3">
          {{ loadingMessage }}
        </p>
        
        <!-- Progress Steps -->
        <div class="progress-steps">
          <div 
            v-for="(step, index) in progressSteps" 
            :key="index"
            class="step-item"
            :class="{ 'active': currentStep >= index, 'completed': currentStep > index }"
          >
            <v-icon 
              :size="$vuetify.display.mobile ? 14 : 16" 
              :color="currentStep > index ? 'success' : currentStep === index ? 'primary' : 'grey-lighten-1'"
              class="step-icon"
            >
              {{ currentStep > index ? 'mdi-check' : 'mdi-circle-outline' }}
            </v-icon>
            <span 
              class="step-text"
              :class="{ 
                'text-primary': currentStep === index, 
                'text-success': currentStep > index,
                'text-grey-lighten-1': currentStep < index 
              }"
            >
              {{ step }}
            </span>
          </div>
        </div>
      </div>

      <!-- Duck Animation - Hidden on mobile to save space -->
      <div v-if="!$vuetify.display.mobile" class="duck-animation mb-4">
        <img 
          src="/ducksImages/duckLoader.webp" 
          alt="Loading Duck"
          class="duck-image"
          width="48"
          height="48"
        />
        <div class="wave-animation">
          <div class="wave"></div>
          <div class="wave"></div>
          <div class="wave"></div>
        </div>
      </div>

      <!-- Estimated Time -->
      <div class="text-caption text-grey-darken-1 mb-2 md:mb-0">
        <v-icon size="14" class="mr-1">mdi-clock-outline</v-icon>
        Estimated time: {{ estimatedTime }}
      </div>

      <!-- Fun Tips - Only show on desktop or for very long operations -->
      <div v-if="showTips && !$vuetify.display.mobile" class="mt-4 pa-3 bg-blue-lighten-5 rounded-lg">
        <div class="d-flex align-center mb-2">
          <v-icon size="16" color="blue" class="mr-2">mdi-lightbulb-outline</v-icon>
          <span class="text-caption font-weight-bold text-blue-darken-2">Did you know?</span>
        </div>
        <p class="text-caption text-blue-darken-1 mb-0">
          {{ currentTip }}
        </p>
      </div>
    </v-card>
  </v-overlay>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

// Props
interface Props {
  modelValue: boolean;
  type?: 'single' | 'bulk';
  templateName?: string;
  duckCount?: number;
  cruiseName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'single',
  templateName: '',
  duckCount: 0,
  cruiseName: ''
});

// Emits (not used in this component)
defineEmits<{
  'update:modelValue': [value: boolean];
}>();

// State
const currentStep = ref(0);
const tipIndex = ref(0);

// Fun tips for users while waiting
const tips = [
  "QR codes can hold up to 4,296 alphanumeric characters!",
  "The first QR code was created in 1994 for tracking automotive parts.",
  "QR stands for 'Quick Response' - they were designed to be read quickly!",
  "Your duck's QR code contains a unique journey ID for tracking.",
  "Each QR code can be scanned from any angle - pretty clever!",
  "QR codes have built-in error correction - they work even when partially damaged!",
  "The duck tradition on cruises brings joy to thousands of passengers worldwide.",
  "Your duck might travel to amazing places and meet wonderful people!"
];

// Computed properties
const loadingTitle = computed(() => {
  if (props.type === 'bulk') {
    return `Generating ${props.duckCount} QR Codes`;
  }
  return 'Generating QR Code';
});

const loadingMessage = computed(() => {
  if (props.type === 'bulk') {
    const template = props.templateName ? ` with ${props.templateName} template` : '';
    const cruise = props.cruiseName ? ` for ${props.cruiseName}` : '';
    return `Creating your QR codes${template}${cruise}. This may take a moment...`;
  }
  return 'Creating your personalized QR code for download...';
});

const progressSteps = computed(() => {
  if (props.type === 'bulk') {
    return [
      'Initializing QR generation',
      'Processing duck data',
      'Generating QR codes',
      'Applying template styling',
      'Creating download package',
      'Finalizing files'
    ];
  }
  return [
    'Initializing QR generation',
    'Processing duck data',
    'Generating QR code',
    'Applying template styling',
    'Preparing download'
  ];
});

const estimatedTime = computed(() => {
  if (props.type === 'bulk') {
    const baseTime = 15; // Base time for all steps (15.5 seconds total)
    const extraTime = Math.ceil((props.duckCount || 1) * 0.3); // Additional time based on duck count
    const totalTime = baseTime + extraTime;
    return totalTime > 20 ? '15-30 seconds' : `${totalTime}-${totalTime + 5} seconds`;
  }
  return '12-18 seconds'; // Updated for single QR code with more steps
});

const showTips = computed(() => props.type === 'bulk' && props.duckCount > 5);

const currentTip = computed(() => tips[tipIndex.value]);

// Methods
let stepInterval: NodeJS.Timeout | null = null;
let tipInterval: NodeJS.Timeout | null = null;

const startProgressAnimation = () => {
  currentStep.value = 0;
  const stepCount = progressSteps.value.length;
  
  // Define timing for each phase
  const stepTimings = [
    2000,  // Step 0: Initializing QR generation (2s)
    4000,  // Step 1: Processing duck data (3s)
    7000,  // Step 2: Generating QR codes (6s) - longest step
    3000,  // Step 3: Applying template styling (2s)
    2000,  // Step 4: Creating download package/Preparing download (1.5s)
    2000   // Step 5: Finalizing files (1s) - only for bulk
  ];

  let currentStepIndex = 0;

  const progressToNextStep = () => {
    if (currentStepIndex < stepCount - 1) {
      currentStepIndex++;
      currentStep.value = currentStepIndex;
      
      // Set timeout for next step if there are more steps
      if (currentStepIndex < stepCount - 1) {
        const nextStepDelay = stepTimings[currentStepIndex] || 2000;
        stepInterval = setTimeout(progressToNextStep, nextStepDelay);
      }
    }
  };

  // Start with first step timing
  const firstStepDelay = stepTimings[0] || 2000;
  stepInterval = setTimeout(progressToNextStep, firstStepDelay);
};

const startTipRotation = () => {
  if (showTips.value) {
    tipInterval = setInterval(() => {
      tipIndex.value = (tipIndex.value + 1) % tips.length;
    }, 4000); // Change tip every 4 seconds
  }
};

const cleanup = () => {
  if (stepInterval) {
    clearTimeout(stepInterval);
    stepInterval = null;
  }
  if (tipInterval) {
    clearInterval(tipInterval);
    tipInterval = null;
  }
};

// Watchers
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    startProgressAnimation();
    startTipRotation();
  } else {
    cleanup();
  }
});

// Lifecycle
onMounted(() => {
  if (props.modelValue) {
    startProgressAnimation();
    startTipRotation();
  }
});

onUnmounted(() => {
  cleanup();
});
</script>

<style scoped>
.loading-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(59, 130, 246, 0.1);
}

.loading-animation-container {
  position: relative;
  display: inline-block;
  margin: 0 auto;
}

.qr-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-icon-animation {
  animation: pulse-scale 2s ease-in-out infinite;
  z-index: 2;
}

.progress-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.download-icon-float {
  position: absolute;
  top: -10px;
  right: -10px;
  background: white;
  border-radius: 50%;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: float-bounce 2s ease-in-out infinite;
}

.download-animation {
  animation: download-pulse 1.5s ease-in-out infinite;
}

.progress-steps {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 12px 0;
}

@media (min-width: 768px) {
  .progress-steps {
    gap: 8px;
    margin: 16px 0;
  }
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .step-item {
    gap: 12px;
    padding: 4px 0;
  }
}

.step-item.active .step-icon {
  animation: step-pulse 0.6s ease-out;
}

.step-item.completed .step-text {
  text-decoration: line-through;
  opacity: 0.7;
}

.step-text {
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .step-text {
    font-size: 0.875rem;
  }
}

.duck-animation {
  position: relative;
  display: inline-block;
}

.duck-image {
  animation: duck-float 3s ease-in-out infinite;
  z-index: 2;
  position: relative;
}

.wave-animation {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2px;
}

.wave {
  width: 4px;
  height: 8px;
  background: linear-gradient(to bottom, #3b82f6, #1e40af);
  border-radius: 2px;
  animation: wave-motion 1.5s ease-in-out infinite;
}

.wave:nth-child(2) {
  animation-delay: 0.2s;
}

.wave:nth-child(3) {
  animation-delay: 0.4s;
}

/* Animations */
@keyframes pulse-scale {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes float-bounce {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes download-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

@keyframes step-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes duck-float {
  0%, 100% {
    transform: translateY(0px) rotate(-2deg);
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
  }
}

@keyframes wave-motion {
  0%, 100% {
    height: 4px;
    opacity: 0.6;
  }
  50% {
    height: 12px;
    opacity: 1;
  }
}

/* Dark mode support */
.v-theme--dark .loading-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.v-theme--dark .download-icon-float {
  background: #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
</style>
